import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Calculator } from "@phosphor-icons/react";
import { inr } from "@/lib/gate";

const inp = "min-w-0 w-full h-9 bg-slate-800/60 border border-slate-600 text-white rounded-sm px-2 text-sm font-mono-num disabled:opacity-60";
const SECTIONS = [
  { k: "iron", title: "IRON (pipe / channel / angle)" },
  { k: "hardware", title: "HARDWARE USED (internal, stock se katega)" },
  { k: "labour", title: "LABOUR (welder / fitting)" },
  { k: "other", title: "OTHER (paint, transport…)" },
];

// Admin sees rates, costs and profit. Secondary Admin enters quantities only.
export default function GateCosting({ order, isAdmin }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [hw, setHw] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data: d } = await api.get(`/orders/${order.id}/gate-costing`);
      setData(d);
      setDraft(Object.fromEntries(d.work_items.map((w) => [w.work_item_id, {
        iron: w.iron.map((r) => ({ ...r })), labour: w.labour.map((r) => ({ ...r })), other: w.other.map((r) => ({ ...r })),
        hardware: w.hardware.map((r) => ({ ...r, amount: r.unit_cost })),
      }])));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [order.id, order.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/items").then(({ data: its }) => setHw((its || []).filter((i) => String(i.category || "").toLowerCase() === "hardware" && i.is_active !== false))).catch(() => {});
  }, []);
  if (!data) return null;
  const locked = data.locked && !isAdmin;
  const set = (wid, k, i, f, v) => setDraft((d) => ({ ...d, [wid]: { ...d[wid], [k]: d[wid][k].map((r, j) => (j === i ? { ...r, [f]: v } : r)) } }));
  const add = (wid, k) => setDraft((d) => ({ ...d, [wid]: { ...d[wid], [k]: [...d[wid][k], k === "iron" ? { kg: "", rate: data.default_iron_rate ?? "" } : {}] } }));
  const del = (wid, k, i) => setDraft((d) => ({ ...d, [wid]: { ...d[wid], [k]: d[wid][k].filter((_, j) => j !== i) } }));
  const save = async () => {
    setBusy(true);
    try {
      const num = (v) => (v === "" || v == null ? null : Number(v));
      await api.put(`/orders/${order.id}/gate-costing`, { work_items: Object.entries(draft).map(([wid, c]) => ({
        work_item_id: wid,
        iron: c.iron.map((r) => ({ id: r.id, kg: num(r.kg), rate: isAdmin ? num(r.rate) : null })),
        hardware: c.hardware.map((r) => ({ id: r.id, item_id: r.item_id, qty: num(r.qty), amount: isAdmin ? num(r.amount) : null })),
        labour: c.labour.map((r) => ({ id: r.id, amount: num(r.amount), note: r.note || "" })),
        other: c.other.map((r) => ({ id: r.id, amount: num(r.amount), note: r.note || "" })),
      })) });
      toast.success("Costing saved ✓");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const S = data.summary;
  return (
    <div className="surface p-4 rounded-sm border-l-4 border-amber-500 space-y-3" data-testid="gate-costing">
      <div className="flex items-center gap-2">
        <Calculator size={16} className="text-amber-300" />
        <span className="text-[10px] font-mono-num tracking-widest text-amber-300">// GATE COSTING — {isAdmin ? "admin only" : "quantities only"}</span>
        {data.locked && <span className="text-[10px] font-mono-num text-slate-400 ml-auto">DELIVERED {isAdmin ? "— admin edit" : "— locked"}</span>}
      </div>
      {isAdmin && S && (
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center" data-testid="gate-costing-summary">
          {[["Bill", S.bill], ["Iron", S.iron], ["Plate", S.plate], ["Hardware", S.hardware], ["Labour", S.labour], ["Other", S.other], ["Commission", S.commission], ["PROFIT", S.profit]].map(([l, v]) => (
            <div key={l} className={`surface-2 p-2 rounded-sm ${l === "PROFIT" ? (v >= 0 ? "border border-emerald-500" : "border border-red-500") : ""}`}>
              <div className="text-[9px] font-mono-num text-slate-400 tracking-widest">{l.toUpperCase()}</div>
              <div className={`font-mono-num text-sm ${l === "PROFIT" ? "text-emerald-300 font-black" : "text-slate-100"}`} data-testid={`gate-costing-${l.toLowerCase()}`}>{inr(v)}</div>
            </div>
          ))}
        </div>
      )}
      <datalist id="gate-hw-list">{hw.map((m) => <option key={m.id} value={m.name} />)}</datalist>
      {isAdmin && data.plate_cost_missing?.length > 0 && (
        <div className="text-[11px] text-amber-300" data-testid="gate-costing-plate-missing">Plate cost recorded nahi hai: {data.plate_cost_missing.join(", ")} — profit zyada dikh sakta hai. Sheet purchase entry karein.</div>
      )}
      {data.work_items.map((w) => (
        <div key={w.work_item_id} className="surface-2 p-3 rounded-sm space-y-2" data-testid={`costing-wi-${w.work_item_id}`}>
          <div className="flex items-center gap-2 text-xs">
            <span className="font-heading tracking-widest text-slate-200">{w.item_name}</span>
            <span className="ml-auto font-mono-num text-cyan-300">Plate {w.plate_sqft} sqft{isAdmin && w.summary ? ` · ${inr(w.summary.plate)}` : ""}</span>
          </div>
          {SECTIONS.map(({ k, title }) => (
            <div key={k} className="space-y-1">
              <div className="text-[10px] font-mono-num tracking-widest text-slate-400">{title}</div>
              {(draft[w.work_item_id]?.[k] || []).map((r, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`costing-${k}-${w.work_item_id}-${i}`}>
                  {k === "iron" && <>
                    <input type="number" placeholder="kg" value={r.kg ?? ""} disabled={locked} onChange={(e) => set(w.work_item_id, k, i, "kg", e.target.value)} className={inp} data-testid={`costing-iron-kg-${i}`} />
                    {isAdmin && <input type="number" placeholder="₹/kg" value={r.rate ?? ""} onChange={(e) => set(w.work_item_id, k, i, "rate", e.target.value)} className={inp} data-testid={`costing-iron-rate-${i}`} />}
                    {isAdmin && <span className="font-mono-num text-xs text-slate-300 w-24 text-right">{inr((Number(r.kg) || 0) * (Number(r.rate) || 0))}</span>}
                  </>}
                  {k === "hardware" && <>
                    <input list="gate-hw-list" placeholder="Search hardware…" disabled={locked}
                      defaultValue={hw.find((x) => x.id === r.item_id)?.name || r.item_name || ""}
                      onChange={(e) => { const m = hw.find((x) => x.name === e.target.value); if (!m) return; set(w.work_item_id, k, i, "item_id", m.id); if (isAdmin) set(w.work_item_id, k, i, "amount", m.purchase_price ?? ""); }}
                      className={inp} data-testid={`costing-hw-item-${i}`} />
                    <input type="number" placeholder="qty" value={r.qty ?? ""} disabled={locked} onChange={(e) => set(w.work_item_id, k, i, "qty", e.target.value)} className={`${inp} max-w-[90px]`} data-testid={`costing-hw-qty-${i}`} />
                    {isAdmin && <input type="number" placeholder="₹/pc" value={r.amount ?? ""} onChange={(e) => set(w.work_item_id, k, i, "amount", e.target.value)} className={`${inp} max-w-[110px]`} data-testid={`costing-hw-price-${i}`} />}
                  </>}
                  {(k === "labour" || k === "other") && <>
                    {(isAdmin || !r.id) ? (
                      <input type="number" placeholder="₹ amount" value={r.amount ?? ""} disabled={locked} onChange={(e) => set(w.work_item_id, k, i, "amount", e.target.value)} className={`${inp} max-w-[140px]`} data-testid={`costing-${k}-amount-${i}`} />
                    ) : <span className="text-[11px] text-slate-500 w-[140px]">saved</span>}
                    <input placeholder="note" value={r.note || ""} disabled={locked} onChange={(e) => set(w.work_item_id, k, i, "note", e.target.value)} className={inp} data-testid={`costing-${k}-note-${i}`} />
                  </>}
                  {!locked && <button onClick={() => del(w.work_item_id, k, i)} className="text-red-400 p-1"><Trash size={14} /></button>}
                </div>
              ))}
              {!locked && <button onClick={() => add(w.work_item_id, k)} className="h-7 px-2 border border-slate-600 text-slate-300 rounded-sm text-[10px] font-mono-num flex items-center gap-1" data-testid={`costing-add-${k}-${w.work_item_id}`}><Plus size={10} /> ADD</button>}
            </div>
          ))}
        </div>
      ))}
      {!locked && <button onClick={save} disabled={busy} className="h-10 px-4 rounded-sm bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-heading tracking-widest disabled:opacity-50" data-testid="save-gate-costing-btn">SAVE COSTING</button>}
    </div>
  );
}
